import { useEffect, useRef } from "react"

import { AnimatePresence, motion } from "motion/react"
import { useTranslation } from "react-i18next"

import type { RoutedModelInfo, SourcesByType, SourceType } from "../../utils/activity-message-utils"

import { Brain, FileText, Globe, History, Sparkles } from "@/components/icons"

// Minimum gap between two success pulses on the icon ring. Back-to-back
// activities arriving within the animation window get coalesced so the user
// sees a single ring rather than a visibly overlapping double pulse.
const PULSE_MIN_INTERVAL_MS = 700

// Render status markdown (bold only) without injecting HTML.
//
// `shownRaw` is the number of raw characters from `fullText` that should be
// visible. Markdown
// markers (`**`) count toward raw length but do not render themselves.
function renderPartialBoldMarkdown(fullText: string, shownRaw: number): React.ReactNode {
  const output: React.ReactNode[] = []
  let cursor = 0
  let keyIndex = 0

  while (cursor < fullText.length && cursor < shownRaw) {
    if (fullText.startsWith("**", cursor)) {
      const closingIdx = fullText.indexOf("**", cursor + 2)
      if (closingIdx === -1) {
        // Unclosed opener — render remaining revealed chars as bold so the
        // user sees the styling begin before the closer has arrived.
        const boldStart = cursor + 2
        if (shownRaw > boldStart) {
          output.push(<strong key={keyIndex++}>{fullText.slice(boldStart, shownRaw)}</strong>)
        }
        return output
      }
      const boldContent = fullText.slice(cursor + 2, closingIdx)
      const boldEndRaw = closingIdx + 2
      if (shownRaw >= boldEndRaw) {
        output.push(<strong key={keyIndex++}>{boldContent}</strong>)
        cursor = boldEndRaw
      } else {
        const visibleChars = Math.max(0, shownRaw - (cursor + 2))
        if (visibleChars > 0) {
          output.push(<strong key={keyIndex++}>{boldContent.slice(0, visibleChars)}</strong>)
        }
        return output
      }
    } else {
      const nextBoldIdx = fullText.indexOf("**", cursor)
      const segEnd = nextBoldIdx === -1 ? fullText.length : nextBoldIdx
      const plainEnd = Math.min(segEnd, shownRaw)
      output.push(<span key={keyIndex++}>{fullText.slice(cursor, plainEnd)}</span>)
      if (plainEnd < segEnd) return output
      cursor = segEnd
    }
  }

  return output
}

type ActivityMessageHeaderProps = {
  totalActivities: number
  latestActivityText?: string
  currentGroupTitle?: string
  isLoading?: boolean
  activeIcon?: React.ReactNode
  sourcesByType?: SourcesByType
  isMasking?: boolean
  /** Explorer Auto model+effort routing decision (#2436), once known. Shown
   *  in the header row (not just inside the expanded item list) so it
   *  survives the accordion collapsing — the header is always visible. */
  routedModelInfo?: RoutedModelInfo
}

// Per-source-type visuals for the collapsed chip row. `order` pins a stable
// left-to-right layout so chips don't reshuffle as new types come online.
const SOURCE_TYPE_CONFIG: Record<
  SourceType,
  { icon: React.ReactNode; labelKey: string; fallbackLabel: string; order: number }
> = {
  web: {
    icon: <Globe className="size-3" strokeWidth={2} />,
    labelKey: "activity_messages.source_type_web",
    fallbackLabel: "web",
    order: 0,
  },
  documents: {
    icon: <FileText className="size-3" strokeWidth={2} />,
    labelKey: "activity_messages.source_type_documents",
    fallbackLabel: "documents",
    order: 1,
  },
  research: {
    icon: <Brain className="size-3" strokeWidth={2} />,
    labelKey: "activity_messages.source_type_research",
    fallbackLabel: "research",
    order: 2,
  },
}

export function ActivityMessageHeader({
  totalActivities,
  latestActivityText,
  currentGroupTitle,
  isLoading,
  activeIcon,
  sourcesByType,
  isMasking = false,
  routedModelInfo,
}: ActivityMessageHeaderProps) {
  const { t } = useTranslation()
  const iconCircleRef = useRef<HTMLElement | null>(null)
  const prevActivitiesRef = useRef(totalActivities)
  const lastPulseAtRef = useRef(0)

  // Function to strip markdown headers, they look odd in the header
  const stripMarkdownHeaders = (text: string) => {
    return text.replace(/^#{1,6}\s*/gm, "").trim()
  }

  const title = isLoading
    ? latestActivityText && stripMarkdownHeaders(latestActivityText)
    : (currentGroupTitle ?? t("activity_history.title", { count: totalActivities }))

  // Change B: pulse the icon circle when a new activity arrives.
  // Skip during TTFT masking — the breathing pulse from C1 covers that period.
  // Coalesce pulses that land within PULSE_MIN_INTERVAL_MS so two activities
  // arriving back-to-back don't visibly stack into a double ring.
  useEffect(() => {
    const prev = prevActivitiesRef.current
    prevActivitiesRef.current = totalActivities

    if (!(totalActivities > prev && totalActivities > 0 && !isMasking)) {
      return
    }

    const now = performance.now()
    if (now - lastPulseAtRef.current < PULSE_MIN_INTERVAL_MS) {
      return
    }
    lastPulseAtRef.current = now

    const node = iconCircleRef.current
    if (!node) return

    node.classList.remove("success-pulse")
    // Force reflow so re-adding the class restarts the animation
    void node.offsetWidth
    node.classList.add("success-pulse")
    const timeoutId = window.setTimeout(() => {
      node.classList.remove("success-pulse")
    }, 600)
    return () => window.clearTimeout(timeoutId)
  }, [totalActivities, isMasking])

  // Change A: accumulation metrics line. Hide during TTFT masking when nothing happened yet.
  const showMetrics = totalActivities > 0
  const activitiesLabel =
    totalActivities === 1
      ? t("activity_messages.activities_singular", { defaultValue: "1 activity" })
      : t("activity_messages.activities_plural", {
          count: totalActivities,
          defaultValue: "{{count}} activities",
        })
  const metricsParts: string[] = []
  if (currentGroupTitle) metricsParts.push(currentGroupTitle)
  metricsParts.push(activitiesLabel)
  const metricsText = metricsParts.join(" \u00B7 ")

  // Ordered, non-empty source-type entries rendered inline at the end of the
  // metrics line. Types are pinned to SOURCE_TYPE_CONFIG.order so chips don't
  // shuffle as new ones light up.
  const sourceChips = (Object.entries(sourcesByType ?? {}) as [SourceType, number][])
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => SOURCE_TYPE_CONFIG[a].order - SOURCE_TYPE_CONFIG[b].order)

  return (
    <section className="flex w-full min-w-0 items-center justify-between">
      <section className="flex w-full items-center gap-2.5">
        {/* Keep the text aligned when the initial masking phase ends. */}
        <div className="size-8 shrink-0">
          <AnimatePresence initial={false}>
            {!isMasking && (
              <motion.section
                key="icon-ring"
                ref={iconCircleRef}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-primary/10 relative flex size-8 shrink-0 items-center justify-center rounded-full"
                exit={{ opacity: 0, scale: 0.8 }}
                initial={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <AnimatePresence mode="popLayout">
                  {isLoading ? (
                    <motion.div
                      key="active-icon"
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute inset-0 flex items-center justify-center"
                      exit={{ opacity: 0, scale: 0.8 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                      {/* Background spinning loader */}
                      <div
                        aria-live="polite"
                        className="absolute inset-0 flex items-center justify-center"
                        role="status"
                      >
                        <span
                          aria-hidden
                          className="brand-focus-ring text-primary absolute inset-0"
                        />
                        <span className="sr-only">Loading</span>
                      </div>
                      {/* Foreground active icon */}
                      <div className="[&>svg]:text-primary relative z-10 flex items-center justify-center [&>svg]:size-4">
                        {activeIcon}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="history-icon"
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute inset-0 flex items-center justify-center"
                      exit={{ opacity: 0, scale: 0.8 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                      <History className="text-primary size-4" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
        <section className="flex w-full min-w-0 flex-col gap-0.5 text-left">
          <div className="text-muted-foreground min-h-[1.25em] text-sm leading-tight font-medium">
            <div className={isLoading ? "line-clamp-1 text-wrap" : undefined}>
              {renderPartialBoldMarkdown(title || "", (title || "").length)}
            </div>
          </div>
          {/* Reserve one metrics line so model/status updates do not move the answer. */}
          <div className="min-h-5">
            {showMetrics && (
              <div className="text-muted-foreground flex min-h-5 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 overflow-hidden text-xs leading-tight">
                <span className="line-clamp-1">{metricsText}</span>
                {routedModelInfo && (routedModelInfo.model || routedModelInfo.effort) && (
                  <span className="bg-primary/10 text-primary/80 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] leading-none font-medium">
                    <Sparkles className="size-3" strokeWidth={2} />
                    {routedModelInfo.model && <span>{routedModelInfo.model}</span>}
                    {routedModelInfo.effort && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="capitalize">{routedModelInfo.effort}</span>
                      </>
                    )}
                  </span>
                )}
                <AnimatePresence initial={false}>
                  {sourceChips.map(([type, count]) => {
                    const config = SOURCE_TYPE_CONFIG[type]
                    const label = t(config.labelKey, { defaultValue: config.fallbackLabel })
                    return (
                      <motion.span
                        key={type}
                        animate={{ opacity: 1, scale: 1 }}
                        aria-label={`${count} ${label}`}
                        className="bg-primary/10 text-primary/80 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] leading-none font-medium"
                        exit={{ opacity: 0, scale: 0.8 }}
                        initial={{ opacity: 0, scale: 0.7 }}
                        transition={{ type: "spring", stiffness: 480, damping: 28 }}
                      >
                        {config.icon}
                        {/* Re-key the count so it pops each time it ticks up. */}
                        <motion.span
                          key={count}
                          animate={{ opacity: 1, y: 0 }}
                          className="tabular-nums"
                          initial={{ opacity: 0, y: -3 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                        >
                          {count}
                        </motion.span>
                        <span className="sr-only">{label}</span>
                      </motion.span>
                    )
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>
        </section>
      </section>
    </section>
  )
}
